import React, { useState, useMemo, useContext } from 'react';
import _ from 'lodash';
import styled from 'styled-components/macro';
import { fontDefaultAlt } from '@mirinae/react-ui';

import { FormattedKoPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { playTTS } from '@mirinae/lessons/modules/libs/lessonLib';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import { os } from '@mirinae/js-utils/utils/userAgent';

import ManHover from '@mirinae/lessons/assets/images/man-hovered.svg';
import ManPlaying from '@mirinae/lessons/assets/images/man-playing.svg';
import ManReady from '@mirinae/lessons/assets/images/man-ready.svg';
import WomanHover from '@mirinae/lessons/assets/images/woman-hovered.svg';
import WomanPlaying from '@mirinae/lessons/assets/images/woman-playing.svg';
import WomanReady from '@mirinae/lessons/assets/images/woman-ready.svg';
import CantListenIcon from '@mirinae/lessons/assets/images/cantListen.svg';
import CanListenIcon from '@mirinae/lessons/assets/images/canListen.svg';
import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';
import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import GTM from '@mirinae/apis/GoogleAnalytics';
import Localization from '@mirinae/shared/il8n';

const PracticeReadingElement = styled(Element)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: start;
`;

const AnswerStage = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    row-gap: ${props => (os.isOthers || props.mode === 'read' ? 15 : 30)}px;
    column-gap: ${props => (props.mode === 'read' ? 40 : 40)}px;
    margin: ${props => (props.mode === 'read' ? '0px 0 15px 0' : `${os.isOthers ? 40 : 38}px 0 15px 0`)};
`;

const ListenControl = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    color: var(--contents-a-5-d-6-d-3-start, #A5D6D3);
    font-size: 14px;
    font-family: ${fontDefaultAlt};
    font-weight: 700;
    margin-top: 20px;
    gap: 10px;
    cursor: pointer;
`;

const TargetSentence = styled.div`
    display: flex;
    flex-direction: row;
    cursor: pointer;
    align-items: center;
    height: ${os.isOthers ? '85px' : 'fit-content'};
    margin-bottom: ${os.isOthers ? 0 : 7}px;
    padding-bottom: ${props => (props.mode === 'read' ? 20 : 0)}px;
    margin-top: ${props => (props.mode === 'listen' ? 20 : 0)}px;
  
    svg {
        transform: scale(${os.isOthers ? 1.5 : 1.4});
    }
  `;

const ChoiceWrapper = styled.div`
    position: relative;
`;

const ChoiceButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 57px;
    height: 57px;
    border-radius: 10px;
    border: 1px solid var(--gray-dfdfdf, #DFDFDF);
    background: var(--primary-ffffff-white, #FFF);
    box-shadow: 3px 3px 7px 0px rgba(159, 188, 186, 0.40);
    color: var(--gray-3-a-3-a-3-a, #3A3A3A);
    font-size: 18px;
    font-weight: 400;
    
    :hover {
        background: rgba(212, 233, 230, 0.4);
        border: 1px solid #48A9A6;
        box-shadow: none;
    }
    
    :disabled {
        ${props => (props.correct ? (`
            background: rgba(212, 233, 230, 0.4);
            border: 1px solid #48A9A6;
            box-shadow: none;
        `) : (`
            color: #DFDFDF;
            border: 1px solid var(--gray-dfdfdf, #DFDFDF);
            background: var(--primary-ffffff-white, #FFF);
            box-shadow: none;
        `))};
        
        :hover {
          
        }
    }
`;

const ListenAnswer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;
`;

const BadListenAnswer = styled(ListenAnswer)`

    ${ChoiceButton} {
        border-color: ${lessonsPalette.lessons[2]};
        background-color: ${lessonsPalette.lessons[3]};
        box-shadow: none;
    
        &:hover {
            border-color: ${lessonsPalette.lessons[2]};
            background-color: ${lessonsPalette.lessons[3]};
        }
    }

    animation: wiggle 0.25s;
    @keyframes wiggle {
        0% { transform: rotate(0deg); }
       20% { transform: rotate(-10deg); }
       80% { transform: rotate(10deg); }
      100% { transform: rotate(0deg); }
    }
`;

const ReadAnswer = styled.button`
    display: flex;
    flex-direction: row;
    //cursor: pointer;
    align-items: center;
    width: ${os.isOthers ? '320px' : 'calc(100vw - 98px)'};
    height: 70px;
    padding: 0 6px;
    background: var(--primary-ffffff-white, #FFF);
  
    justify-content: center;
    border: 1px solid #DFDFDF;
    box-shadow: 3px 3px 7px rgba(159, 188, 186, 0.4);
    border-radius: 10px;
  
    :hover {
        background: rgba(212, 233, 230, 0.4);
        border: 1px solid #48A9A6;
        box-shadow: none;
    }
  
    :disabled {
        ${props => (props.correct ? (`
            background: rgba(212, 233, 230, 0.4);
            border: 1px solid #48A9A6;
            box-shadow: none;
        `) : (`
            .phrase-word {
                color: #DFDFDF;
            }
            border: 1px solid var(--gray-dfdfdf, #DFDFDF);
            background: var(--primary-ffffff-white, #FFF);
            box-shadow: none;
        `))};
        
        :hover {
          
        }
    }
`;

const BadReadAnswer = styled(ReadAnswer)`
    border-color: ${lessonsPalette.lessons[2]};
    background-color: ${lessonsPalette.lessons[3]};
    animation: wiggle 0.25s;
    box-shadow: none;

    &:hover {
        border-color: ${lessonsPalette.lessons[2]};
        background-color: ${lessonsPalette.lessons[3]};
    }

    @keyframes wiggle {
        0% { transform: rotate(0deg); }
        5% { transform: rotate(-5deg); }
       80% { transform: rotate(5deg); }
      100% { transform: rotate(0deg); }
    }
`;

const AnswerText = styled.div`
    display: flex;
    flex-direction: row;
    cursor: pointer;
    align-items: center;
`;

const SpeakButton = styled.div`
    cursor: pointer;
    svg {
        transform: scale(1.23);
    }
`;

const targetSentenceStyle = { koFontWeight: '500', koreanFontSize: '22px', align: 'center' };
const answerSentenceStyle = { koFontWeight: '400', koreanFontSize: '18px', align: 'center' };

const PracticeListening = ({ element }) => {
    const [audioEl, setAudioEl] = useState(null);
    const [mode, setMode] = useState('listen'); // 'read'
    const [playing, setPlaying] = useState(null);
    const [iconMode, setIconMode] = useState('ready');
    const [iconHover, setIconHover] = useState(false);
    const [badPick, setBadPick] = useState(null);
    const [complete, setComplete] = useState(false);
    const player = useContext(PlayerContext);

    const icons = {
        'Playing': () => (element.fieldsByName.gender.value === 'Female' ? <img src={WomanPlaying} alt="Woman playing" /> : <img src={ManPlaying} alt="Man playing" />),
        'Ready': () => (element.fieldsByName.gender.value === 'Female' ? <img src={WomanReady} alt="Woman ready" /> : <img src={ManReady} alt="Man ready" />),
        'Hover': () => (element.fieldsByName.gender.value === 'Female' ? <img src={WomanHover} alt="Woman hover" /> : <img src={ManHover} alt="Man hover" />),
    };

    const pickAnswer = (answer) => {
        if (!complete) {
            setBadPick(null);
            if (answer === element.fieldsByName.correctAnswer.value) {
                setComplete(true);
                player.postEvent({ action: 'complete' }, element);
                GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
            } else {
                setBadPick(answer);
                player.postEvent({ action: 'error' }, element);
                GTM.userAction('error', { page: player.currentPage.componentName }, 'lessons');
            }
        }
    };

    const fullPhrase = (field) => _.reduce(field.parts.koreanParts, (prev, curr) => prev + curr.value, '');

    const playAnswer = (e, answer) => {
        playTTS(audioEl, fullPhrase(element.fieldsByName[`answer${answer}`]), e, { speed: '80%', speaker: element.fieldsByName.gender.value.toLowerCase() });
        setPlaying(answer);
        setBadPick(null);
    };

    return (
        <PracticeReadingElement element={element}>
            { complete ? <SuccessOverlay top={120} left={1} bottom={10} height="100%" /> : null }
            <TargetSentence
                mode={mode}
                onClick={() => playTTS(audioEl, fullPhrase(element.fieldsByName.target), null, { speed: '80%', speaker: element.fieldsByName.gender.value.toLowerCase() })}
                onMouseEnter={() => setIconHover(true)}
                onMouseLeave={() => (iconHover ? setIconHover(false) : null)}
            >
                { mode === 'listen' && (playing ? <icons.Ready /> : iconMode === 'Playing' ? <icons.Playing /> : iconMode === 'Ready' ? iconHover ? <icons.Hover /> : <icons.Ready /> : <icons.Ready />) }
                { mode === 'read' && <FormattedKoPhrase phrase={element.fieldsByName.target} phraseStyle={targetSentenceStyle} /> }
            </TargetSentence>

            <AnswerStage mode={mode}>
                { ['1', '2', '3', '4'].map(answer => {
                    const ReadChoice = badPick === answer ? BadReadAnswer : ReadAnswer;
                    const ListenChoice = badPick === answer ? BadListenAnswer : ListenAnswer;
                    const SpeakingMode = answer === playing ? (iconMode === 'Playing' ? icons.Playing : iconMode === 'Ready' ? iconHover ? icons.Hover : icons.Ready : icons.Ready) : icons.Ready;
                    return (
                        <ChoiceWrapper key={answer}>
                            { mode === 'listen' ? (
                                <ListenChoice>
                                    <ChoiceButton
                                        key={answer} onClick={e => pickAnswer(answer)}
                                        disabled={complete}
                                        correct={complete && answer === element.fieldsByName.correctAnswer.value}
                                    >
                                        {{ 1: '(A)', 2: '(B)', 3: '(C)', 4: '(D)' }[answer]}
                                    </ChoiceButton>
                                    <SpeakButton onClick={e => playAnswer(e, answer)}>
                                        <SpeakingMode />
                                    </SpeakButton>
                                </ListenChoice>
                            ) : (
                                <>
                                    <ReadChoice
                                        key={answer} disabled={complete} onClick={e => pickAnswer(answer)}
                                        correct={complete && answer === element.fieldsByName.correctAnswer.value}
                                    >
                                        <AnswerText>
                                            <FormattedKoPhrase phrase={element.fieldsByName[`answer${answer}`]} phraseStyle={answerSentenceStyle} />
                                        </AnswerText>
                                    </ReadChoice>
                                </>
                            )}
                        </ChoiceWrapper>
                    );
                })}
            </AnswerStage>
            { !complete && (
                <ListenControl onClick={() => setMode(mode === 'listen' ? 'read' : 'listen')}>
                    { mode === 'listen' ? (
                        <>
                            <img src={CantListenIcon} alt="Can't listen" />
                            {Localization['player-i-cant-listen-now']}
                        </>
                    ) : (
                        <>
                            <img src={CanListenIcon} alt="Can listen" />
                            {Localization['player-i-can-listen-now']}
                        </>
                    )}
                </ListenControl>
            )}

            <audio
                ref={setAudioEl} 
                onPlay={() => setIconMode('Playing')}
                onEnded={() => { setIconMode('Ready'); setPlaying(null); }}
            />
        </PracticeReadingElement>
    );
};

export default PracticeListening;
